import { appRoutes } from "src/utils/appRoutes/appRoutes";
import promptsByRoute from "./promptsByRoute.json";

type IndexEntry = { params: Record<string, string>; prompts: string[] };

const validRouteKeys = new Set(Object.values(appRoutes).map((r) => r.key));

const index = new Map<string, IndexEntry[]>();
const invalidKeys: string[] = [];

for (const [key, prompts] of Object.entries(
  promptsByRoute as Record<string, string[]>
)) {
  // Reject hash fragments — not supported in route+search keys.
  if (key.includes("#")) {
    invalidKeys.push(key);
    continue;
  }

  const qIdx = key.indexOf("?");
  const routeKey = qIdx < 0 ? key : key.slice(0, qIdx);
  const query = qIdx < 0 ? "" : key.slice(qIdx + 1);

  if (!validRouteKeys.has(routeKey)) {
    invalidKeys.push(key);
    continue;
  }
  // Only one `?` allowed: the split between route and query.
  if (query.includes("?")) {
    invalidKeys.push(key);
    continue;
  }

  const searchParams = new URLSearchParams(query);
  const params: Record<string, string> = {};
  let malformed = false;
  for (const name of new Set(searchParams.keys())) {
    const all = searchParams.getAll(name);
    // Reject duplicate same-name params (`?tab=a&tab=b`) and empty values (`?tab=`).
    if (all.length > 1 || all[0] === "") {
      malformed = true;
      break;
    }
    params[name] = all[0];
  }
  if (malformed) {
    invalidKeys.push(key);
    continue;
  }

  const list = index.get(routeKey) ?? [];
  list.push({ params, prompts });
  index.set(routeKey, list);
}

// Bare entries first so their prompts render before query-specific ones after dedupe.
for (const list of index.values()) {
  list.sort((a, b) => Object.keys(a.params).length - Object.keys(b.params).length);
}

if (invalidKeys.length) {
  console.warn(
    `[AiAssistantPromptsSuggestions] Invalid keys in promptsByRoute.json: ${invalidKeys.join(", ")}`
  );
}

/**
 * Look up static prompts for the current route+search. An entry matches when its
 * declared params all equal the URL's values for those keys; bare entries (no
 * declared params) trivially match. Results are merged across all matched entries
 * (bare-first) and deduped. Returns `undefined` when no entry matches the route.
 */
export const getStaticPromptsForRoute = (
  routeKey: string,
  search: string
): string[] | undefined => {
  const entries = index.get(routeKey);
  if (!entries) return undefined;

  const urlParams = new URLSearchParams(search);
  const matched = entries.filter((e) =>
    Object.entries(e.params).every(([k, v]) => urlParams.get(k) === v)
  );
  if (!matched.length) return undefined;

  return [...new Set(matched.flatMap((e) => e.prompts))];
};
